<template>
  <div>
    <page-header
      header="Property Feeds"
      subheader="Check to see if your feeds are all up-to-date and if there are any known issues."
    />

    <div>
      <div class="flex -mx-6">
        <div class="w-1/3 px-6 flex">
          <div class="p-6 bg-white rounded-lg flex-1">
            <h2 class="font-medium">
              Active Properties
            </h2>

            <loader v-if="loading" class="mt-10" />

            <div v-else>
              <div class="flex flex-col mt-6">
                <div class="flex flex-col">
                  <h2 class="text-4xl font-semibold text-tertiary">
                    {{ items.active_properties_count.active_properties_total }}
                  </h2>
                  <h4 class="font-medium leading-tight text-primary">
                    Active Properties
                  </h4>
                  <small v-if="profile.type <= 2" class="leading-tight">
                    {{ activeProperties.name }}
                  </small>
                </div>
              </div>

              <div class="mt-6">
                <div class="flex justify-between items-center">
                  <span>
                    Sales
                  </span>
                  <span class="ml-auto">
                    {{ items.active_properties_count.sales.total }}
                  </span>
                  <span
                    class="flex justify-center items-center ml-4 w-6 h-6 rounded-full border"
                    :class="[ activeProperties.properties.alerts > 0 ? 'border-red text-red' : 'border-green text-green' ]"
                  >
                    <i
                      class="fas fa-xs"
                      :class="[ activeProperties.properties.alerts > 0 ? 'fa-exclamation' : 'fa-check' ]"
                    />
                  </span>
                  <span class="ml-2 fas">
                    <i  :class="[expand_sales ? 'fa-chevron-up' : 'fa-chevron-down']" @click.stop.prevent="expand_sales = !expand_sales"></i>
                  </span>
                </div>
                <div>
                  <div v-if="expand_sales" class="mt-4">
                    <div  v-for="(count, key) in items.active_properties_count.sales.break_down" :key="key">
                      <div class="flex justify-between items-center" v-if="count">
                        <span class="mb-2 text-sm">{{ key }}: </span>
                        <span class="text-sm">{{ count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flex justify-between items-center mt-2 pt-2 border-t">
                  <span>
                    Lettings
                  </span>
                  <span class="ml-auto">
                    {{ items.active_properties_count.lettings.total }}
                  </span>
                  <span
                    class="flex justify-center items-center ml-4 w-6 h-6 rounded-full border"
                    :class="[ activeProperties.properties.alerts > 0 ? 'border-red text-red' : 'border-green text-green' ]"
                  >
                    <i
                      class="fas fa-xs"
                      :class="[ activeProperties.properties.alerts > 0 ? 'fa-exclamation' : 'fa-check' ]"
                    />
                  </span>
                  <span class="ml-2 fa">
                    <i :class="[expand_letting ? 'fa-chevron-up' : 'fa-chevron-down']" @click.stop.prevent="expand_letting = !expand_letting"></i>
                  </span>
                </div>
                <div>
                  <div v-if="expand_letting" class="mt-4">
                    <div  v-for="(count, key) in items.active_properties_count.lettings.break_down" :key="key">
                      <div class="flex justify-between items-center" v-if="count">
                        <span class="mb-2 text-sm">{{ key }}: </span>
                        <span class="text-sm">{{ count }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-10 flex items-center">
                <span
                  class="flex justify-center items-center w-6 h-6 rounded-full border"
                  :class="[ activeProperties.properties.alerts > 0 ? 'border-red text-red' : 'border-green text-green' ]"
                >
                  <i
                    class="fas fa-xs"
                    :class="[ activeProperties.properties.alerts > 0 ? 'fa-exclamation' : 'fa-check' ]"
                  />
                </span>
                <span
                  class="border-b ml-4"
                  :class="[ activeProperties.properties.alerts > 0 ? 'border-red text-red' : 'border-green text-green' ]"
                >
                  {{ activeProperties.properties.alerts }} Require Attention
                </span>
              </div>
            </div>
          </div>
        </div> <!-- Active Properties -->

        <div class="w-1/3 px-6 flex">
          <div class="p-6 bg-white rounded-lg flex flex-col flex-1">
            <h2 class="font-medium">
              Inbound Feeds
            </h2>

            <loader v-if="loading" class="mt-10" />

            <div v-else-if="!items.inbound_feeds" class="flex flex-1 items-center justify-center">
              <h3 class="font-normal text-4xl text-grey-light text-center">
                No inbound<br>feed setup
              </h3>
            </div>

            <div v-else class="flex flex-col flex-1">
              <div class="flex flex-col flex-1 mt-6" v-for="feed in items.inbound_feeds" :key="feed.branch_id">
                <h2 class="text-2xl font-semibold text-primary leading-tight">
                  {{ feed.provider.name }}
                </h2>
                <h4 class="text-base font-normal leading-tight">
                  <!-- Sales &amp; Lettings (Branch ID: {{ inboundFeed.branch_id }}) -->
                  Branch ID: <span class="font-medium">{{ feed.branch_id }}</span>
                </h4>
                <div class="flex text-xs leading-normal mt-2">
                  <div class="w-1/3">Last Update:</div>
                  <span v-if="feed.last_ran_at" class="text-green">{{ feed.last_ran_at | datetime }}</span>
                  <span v-else class="text-red">Not Processed</span>
                </div>
                <div class="flex text-xs leading-normal mt-2">
                  <div class="w-1/2">Sales:
                    <span>
                      <i v-if="feed.sales" class="fas text-green fa-check"></i>
                      <i v-if="!feed.sales" class="fas text-red fa-times"></i>
                    </span>
                  </div>

                  <div class="w-1/2">Lettings:
                    <span>
                      <i v-if="feed.lettings" class="fas text-green fa-check"></i>
                      <i v-if="!feed.lettings" class="fas text-red fa-times"></i>
                    </span>
                  </div>

                </div>
                <div class="flex text-xs leading-normal mt-2">
                </div>
              </div>
              <div class="mt-6 pt-2 border-t leading-tight tracking-normal">
                <small>
                  <strong class="tracking-wide">Important</strong><br>
                  If your properties are not displaying correctly please first check your feed is set to 'upload to Property Logic'
                </small>
              </div>
            </div>
          </div>
        </div> <!-- Inbound Feeds -->

        <div class="w-1/3 px-6 flex">
          <div class="p-6 bg-white rounded-lg flex flex-col flex-1">
            <h2 class="font-medium">
              Outbound Feeds
            </h2>

            <loader v-if="loading" class="mt-10" />

            <div v-else-if="items.outbound_feeds.length === 0" class="flex flex-1 items-center justify-center">
              <h3 class="font-normal text-4xl text-grey-light text-center">
                No outbound<br>feeds setup
              </h3>
            </div>

            <div v-else class="flex flex-col flex-1 mt-2">
              <div
                v-for="feed in items.outbound_feeds"
                :key="feed.branch_id"
                class="flex flex-col mt-4"
              >
                <h2 class="text-xl font-semibold text-primary leading-tight">
                  {{ feed.provider.name }}
                </h2>
                <h4 class="text-base font-normal leading-tight">
                  Branch ID: <span class="font-medium">{{ feed.branch_id }}</span>
                </h4>
                <div class="flex text-xs leading-normal" v-if="!feed.is_live">
                  <div class="w-1/3">Last Update:</div>
                  <span v-if="feed.last_ran_at" class="text-green">{{ feed.last_ran_at | datetime }}</span>
                  <span v-else class="text-red">Not Processed</span>
                </div>
                <div class="flex text-xs leading-normal" v-if="feed.is_live">
                  <div class="w-1/3">Live feed:</div>
                  <span class="text-green">This is a live feed, properties will be sent to portals as soon as they are updated on the system.</span>
                </div>
              </div>
            </div>
          </div>
        </div> <!-- Outbound Feeds -->
      </div>

      <div class="flex -mx-6 mt-10">
        <div class="w-1/3 px-6">
          <div class="p-6 bg-white rounded-lg h-64">
            <h2 class="font-medium">
              Known Errors
            </h2>

            <loader v-if="loading" class="mt-10" />

            <div v-else class="flex flex-col flex-1 mt-6 text-base">
              <div class="flex items-center">
                <span class="w-4/5">
                  Missing Images
                </span>
                <span>
                  {{ items.missing_images_count }}
                </span>
              </div>
              <div class="flex items-center mt-4 pt-4 border-t">
                <span class="w-4/5">
                  Missing Coordinates
                </span>
                <span>
                  {{ items.missing_coordinates }}
                </span>
              </div>
              <div class="flex items-center mt-4 pt-4 border-t" v-if="selectedOffice.network_id !== 8">
                <span class="w-4/5">
                  Rejected Properties
                </span>
                <span>
                  {{items.rejected_properties}}
                </span>
                <div class="text-red ml-4">
                  <i class="fas fa-exclamation-triangle" />
                </div>
              </div>
            </div>
          </div>
        </div> <!-- Known Errors -->

        <div class="w-2/3 px-6 flex">
          <div class="p-6 bg-white rounded-lg flex flex-col flex-1">
            <h2 class="font-medium">
              Top 10 Recently Listed Properties
            </h2>

            <loader v-if="loading" class="mt-10" />

            <div v-else-if="items.last_ten_properties.length === 0" class="flex flex-1 justify-center items-center my-10">
              <h3 class="font-normal text-4xl text-grey-light">
                No properties listed this week
              </h3>
            </div>

            <div v-else class="flex flex-col flex-1 mt-4">
              <table class="listed-properties">
                <thead>
                  <th class="listed-properties__header text-left">Property</th>
                  <th class="listed-properties__header">Price</th>
                  <th class="listed-properties__header">Uploaded</th>
                  <th class="listed-properties__header">Updated</th>
                  <!-- <th class="listed-properties__header">Status</th> -->
                </thead>
                <tbody>
                  <tr
                    class="listed-properties__row"
                    v-for="property in items.last_ten_properties"
                    :key="property.index"
                  >
                    <td class="listed-properties__data w-1/2">
                      <div class="flex items-center">
                        <img v-if="property.media.length > 0"
                          :src="property.media[0].full_url"
                          :alt="property.display_address"
                          class="h-12 my-1 mr-4"
                        >
                        <div v-else class="mr-4">
                          <div class="relative w-24 h-12 px-6 pt-2 px-2 rounded bg-tertiary">
                            <icon name="property_cms" class="text-white"/>
                          </div>
                        </div>

                        {{ property.display_address }}
                      </div>
                    </td>
                    <td class="listed-properties__data listed-properties__data--centered">
                      {{ property.currency }} {{ property.price }}
                    </td>
                    <td class="listed-properties__data listed-properties__data--small listed-properties__data--centered">
                      {{ property.created_at | date }}
                      <br>
                      {{ property.created_at | time }}
                    </td>
                    <td class="listed-properties__data listed-properties__data--small listed-properties__data--centered">
                      {{ property.updated_at | date }}
                      <br>
                      {{ property.updated_at | time }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- <pre class="code">{{ listedProperties }}</pre> -->
            </div>

            <div
              class="flex justify-center -mb-2"
            >
              <router-link
                to="/property-management"
                class="text-grey-darker uppercase text-xs text-center no-underline border-b mt-4 pb-1 px-2"
              >
                View All
              </router-link>
            </div>
          </div>
        </div> <!-- Listed Properties this week -->
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { get, take } from 'lodash'
import moment from 'moment'
import api from '@/http'

export default {
  data () {
    return {
      // loading: true
      items: [],
      isLoading: false,
      error: null,
      expand_letting: false,
      expand_sales: false
    }
  },

  computed: {
    ...mapState([ 'profile', 'selectedOffice', 'activeProperties' ]),

    loading () {
      return this.activeProperties === null
    },

    propertiesActive () {
      return get(this.activeProperties, 'properties.active', 0)
    },

    inboundFeed () {
      return get(this.activeProperties, 'inbound_feeds')
    },

    outboundFeeds () {
      return get(this.activeProperties, 'outbound_feeds')
    },

    listedProperties () {
      let properties = get(this.activeProperties, 'properties.propertyList', [])
      let cutOff = moment().startOf('week')

      properties = properties.filter(p => moment(p.uploaded_at).isAfter(cutOff))

      return take(properties, 3)
    }
  },

  methods: {
    ...mapActions([ 'fetchActiveProperties' ]),

    fetchItems () {
      api.get(`/feed-stats/${this.selectedOffice.id}`)
        .then((response) => {
          this.loading = true
          this.items = response.data
        })
        .finally(() => {
          this.loading = false
        })
    }
  },
  watch: {
    selectedOffice () {
      this.fetchActiveProperties(this.selectedOffice.id)
      this.fetchItems()
    }
  },

  created () {
    if (this.selectedOffice.id) {
      this.fetchActiveProperties(this.selectedOffice.id)
    }
    this.fetchItems()
  }
}
</script>

<style lang="scss" scoped>
.listed-properties {
  @apply table-auto w-full mt-4;
  &__header {
    @apply text-xs uppercase font-normal pb-2;
  }
  &__row {
    @apply border-t;
  }
  &__data {
    @apply py-2 align-middle text-sm;
    &--small {
      @apply text-xs;
    }
    &--centered {
      @apply text-center;
    }
  }
}
</style>
